import { Component, ChangeDetectionStrategy, output, signal, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  template: `
<div class="relative flex flex-grow items-center justify-center overflow-hidden bg-slate-950 px-4 py-14">
  <div class="absolute inset-0">
    <div class="absolute left-1/2 top-0 h-[380px] w-[380px] -translate-x-1/2 rounded-full bg-blue-600/20 blur-3xl"></div>
    <div class="absolute -left-20 bottom-0 h-[280px] w-[280px] rounded-full bg-indigo-600/15 blur-2xl"></div>
  </div>

  <div class="relative w-full max-w-md rounded-3xl border border-slate-700/70 bg-slate-900/80 p-8 shadow-2xl backdrop-blur">
    <div class="text-center">
      <div class="mx-auto mb-4 flex w-fit items-center gap-3 rounded-2xl border border-slate-700/80 bg-slate-950/80 px-3 py-2">
        <span class="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-indigo-900/30">
          <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none">
            <path d="M16.5 7.5A6.5 6.5 0 1 0 16.5 16.5" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
            <path d="M10 14.5L14.5 10" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </span>
        <span class="text-lg font-black tracking-tight text-white">Correct<span class="text-blue-400">ify</span></span>
      </div>
      <p class="text-xs uppercase tracking-widest text-blue-300">Acesso seguro</p>
      <h1 class="mt-3 text-3xl font-black text-white">Entrar no Correctify</h1>
      <p class="mt-2 text-sm text-slate-400">Acompanhe sua evolução e continue suas correções.</p>
    </div>

    <form class="mt-6 space-y-5" (ngSubmit)="onLogin()" #loginForm="ngForm">
      <div>
        <label for="email-address" class="mb-1 block text-sm font-semibold text-slate-300">E-mail</label>
        <input
          id="email-address"
          name="email"
          type="email"
          autocomplete="email"
          required
          email
          [(ngModel)]="email"
          #emailModel="ngModel"
          class="block w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 placeholder-slate-500 outline-none transition focus:border-blue-500"
          placeholder="seu@email.com">
        @if (emailModel.invalid && (emailModel.dirty || emailModel.touched)) {
          <p class="mt-1 text-xs text-rose-400">Informe um e-mail válido.</p>
        }
      </div>
      <div>
        <label for="password" class="mb-1 block text-sm font-semibold text-slate-300">Senha</label>
        <input
          id="password"
          name="password"
          type="password"
          autocomplete="current-password"
          required
          [(ngModel)]="password"
          class="block w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 placeholder-slate-500 outline-none transition focus:border-blue-500"
          placeholder="Sua senha">
      </div>

      <label class="flex items-start gap-3 rounded-xl border border-slate-700 bg-slate-950/60 p-3 text-sm text-slate-300">
        <input
          type="checkbox"
          [ngModel]="acceptedLegal()"
          (ngModelChange)="acceptedLegal.set(!!$event)"
          name="acceptedLegal"
          class="mt-0.5 h-4 w-4 rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500" />
        <span>
          Li e concordo com os <button type="button" (click)="openTerms()" class="underline text-blue-300 hover:text-blue-200">Termos de Serviço</button> e com a <button type="button" (click)="openPrivacy()" class="underline text-blue-300 hover:text-blue-200">Política de Privacidade</button>.
        </span>
      </label>

      <button
        type="submit"
        [disabled]="isLoggingIn() || !loginForm.form.valid || !acceptedLegal()"
        class="inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3.5 font-bold text-white transition hover:opacity-90 disabled:opacity-60">
        @if (isLoggingIn()) {
          <div class="loader !h-5 !w-5 !border-2 !border-t-white"></div>
          <span class="ml-2">Entrando...</span>
        } @else {
          <span>Entrar</span>
        }
      </button>
    </form>

    <div class="mt-4 text-center">
      <button type="button" (click)="onForgotPassword()" class="text-sm text-blue-300 hover:text-blue-200 underline">
        Esqueceu sua senha?
      </button>
    </div>

    <p class="mt-4 text-center text-sm text-slate-400">
      Ainda não tem conta?
      <button type="button" (click)="goToRegister()" class="font-semibold text-blue-300 hover:text-blue-200">
        Criar conta
      </button>
    </p>
  </div>
</div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
})
export class LoginComponent {
  loginSuccess = output<void>();
  requestRegister = output<void>();

  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  isLoggingIn = signal(false);
  acceptedLegal = signal(false);
  email = '';
  password = '';
  private isDestroyed = false;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.isDestroyed = true;
    });
  }

  async onLogin() {
    if (!this.acceptedLegal()) {
      this.authService.showNotification(
        'info',
        'Consentimento obrigatório',
        'Aceite os Termos de Serviço e a Política de Privacidade para continuar.'
      );
      return;
    }

    this.isLoggingIn.set(true);
    try {
      await this.authService.signIn(this.email, this.password, this.acceptedLegal());
      if (!this.isDestroyed) {
        this.loginSuccess.emit();
      }
    } catch (error: any) {
      if (!this.isDestroyed) {
        const message = error?.message ?? 'Não foi possível realizar o login. Tente novamente.';
        this.authService.showNotification('error', 'Falha no login', message);
      }
    } finally {
      if (!this.isDestroyed) {
        this.isLoggingIn.set(false);
      }
    }
  }

  async onForgotPassword() {
    if (!this.email) {
      this.authService.showNotification('info', 'Informe seu e-mail', 'Digite seu e-mail no campo acima para receber o link de redefinição.');
      return;
    }
    try {
      await this.authService.requestPasswordReset(this.email);
      this.authService.showNotification('success', 'E-mail enviado', 'Verifique sua caixa de entrada para redefinir sua senha.');
    } catch (error: any) {
      this.authService.showNotification('error', 'Erro', error?.message ?? 'Não foi possível enviar o e-mail de redefinição.');
    }
  }

  openTerms() {
    window.open('https://www.usecorrectify.com/#termos', '_blank');
  }

  openPrivacy() {
    window.open('https://www.usecorrectify.com/#privacidade', '_blank');
  }

  goToRegister() {
    this.requestRegister.emit();
  }
}
